import { useState, type CSSProperties, type ReactNode } from 'react';
import { clsx } from '../../../ds/clsx';
import { Avatar, Banner, Button, Dialog } from '../../../ds/components';
import styles from './PublicFrame.module.css';

export interface PublicAdvisor {
  name: string;
  email?: string | null | undefined;
  phone?: string | null | undefined;
  initials?: string | undefined;
}

export interface PublicLegal {
  imprintUrl: string | null;
  privacyUrl: string | null;
}

export interface PublicFrameProps {
  /** Context line next to the wordmark („Reservierung · WE 2.05", „Selbstauskunft"). */
  context: string;
  /** Right end of the header („● Gespeichert · 19:48"). */
  headerEnd?: ReactNode | undefined;
  /** Content width of the centred column (760 for the flows, 620 for the result pages). */
  width?: number | undefined;
  /** A left column (the form's stepper and advisor card, 260 px) beside the content. */
  sidebar?: ReactNode | undefined;
  /** Advisor contact card in the footer („Ihr Ansprechpartner"). */
  advisor?: PublicAdvisor | null | undefined;
  organizationName?: string | null | undefined;
  legal?: PublicLegal | null | undefined;
  /** A brand logo shown instead of the DAMOKLES wordmark (max. 40 px high; `alt` = `brandName`). */
  logoUrl?: string | null | undefined;
  /** A brand colour (`#RRGGBB`) that replaces the accent tokens inside the frame. */
  brandColor?: string | null | undefined;
  /** Name of the brand the logo stands for (the image's alternative text). */
  brandName?: string | null | undefined;
  children: ReactNode;
}

const brandColorFormat = /^#[0-9A-Fa-f]{6}$/;

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return `${parts[0]?.charAt(0) ?? ''}${parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : ''}`.toUpperCase();
}

/**
 * Lean frame of the public flows (PLAN.md §7): wordmark, context line, the content column (mobile first,
 * 390 px → the design's 1440 px), the advisor card and the organization's imprint/privacy links.
 */
export function PublicFrame({
  context,
  headerEnd,
  width = 760,
  sidebar,
  advisor,
  organizationName,
  legal,
  logoUrl,
  brandColor,
  brandName,
  children,
}: PublicFrameProps) {
  const branded = brandColor && brandColorFormat.test(brandColor) ? brandColor : null;
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo = Boolean(logoUrl) && !logoFailed;
  return (
    <div
      className={clsx(styles.page, branded !== null && styles.branded)}
      style={branded !== null ? ({ '--brand': branded } as CSSProperties) : undefined}
    >
      <header className={styles.header}>
        {showLogo && logoUrl ? (
          <img
            className={styles.logo}
            src={logoUrl}
            alt={brandName ?? organizationName ?? ''}
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <span className={styles.wordmark}>DAMOKLES</span>
        )}
        <span className={styles.divider} aria-hidden="true" />
        <span className={styles.context}>{context}</span>
        {headerEnd ? <span className={styles.headerEnd}>{headerEnd}</span> : null}
      </header>
      <main className={clsx(styles.main, Boolean(sidebar) && styles.mainWithSidebar)}>
        {sidebar ? <aside className={styles.sidebar}>{sidebar}</aside> : null}
        <div className={styles.column} style={{ maxWidth: width }}>
          {children}
        </div>
      </main>
      {advisor || legal?.imprintUrl || legal?.privacyUrl || organizationName ? (
        <footer className={styles.footer}>
          <div className={styles.footerInner} style={{ maxWidth: sidebar ? 1068 : width }}>
            {advisor ? (
              <div className={styles.advisor}>
                <Avatar
                  initials={advisor.initials ?? initialsOf(advisor.name)}
                  name={advisor.name}
                  size={32}
                />
                <span className={styles.advisorText}>
                  <span className={styles.advisorName}>{advisor.name}</span>
                  <span className={styles.advisorMeta}>
                    Ihr Ansprechpartner
                    {advisor.phone ? ` · ${advisor.phone}` : ''}
                    {advisor.email ? ` · ${advisor.email}` : ''}
                  </span>
                </span>
              </div>
            ) : null}
            <div className={styles.legal}>
              {organizationName ? <span className={styles.organization}>{organizationName}</span> : null}
              {legal?.imprintUrl ? <LegalLink url={legal.imprintUrl} title="Impressum" /> : null}
              {legal?.privacyUrl ? <LegalLink url={legal.privacyUrl} title="Datenschutzhinweise" /> : null}
            </div>
          </div>
        </footer>
      ) : null}
    </div>
  );
}

/**
 * The organization's legal text: served as JSON (`{ type, content }`) for a stored text — shown in a
 * dialog — or as a PDF, which opens in a new tab.
 */
export function LegalLink({
  url,
  title,
  className,
}: {
  url: string;
  title: string;
  className?: string | undefined;
}) {
  const [content, setContent] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function show() {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json, application/pdf' } });
      const type = response.headers.get('Content-Type') ?? '';
      if (!response.ok) {
        setError('Der Text ist derzeit nicht verfügbar.');
        setOpen(true);
        return;
      }
      if (type.includes('application/json')) {
        const body = (await response.json()) as { content?: string };
        setContent(body.content ?? '');
        setOpen(true);
        return;
      }
      window.open(url, '_blank', 'noopener');
    } catch {
      setError('Der Text ist derzeit nicht verfügbar.');
      setOpen(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={clsx(styles.legalLink, className)}
        onClick={() => void show()}
        disabled={loading}
      >
        {title}
      </button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={title}
        overlayTop={0}
        footer={
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Schließen
          </Button>
        }
      >
        {error ? <Banner tone="danger">{error}</Banner> : <pre className={styles.legalText}>{content}</pre>}
      </Dialog>
    </>
  );
}
